import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { lowerCase } from "lodash";
import { useLocation } from "react-router";
import { FEATURE_FLAG_NAME } from "common/constants";
import { buildFullLink, buildLink, getPath } from "common/routing";
import { arrayHasExactlyOneItem } from "common/utils/arrays";
import { SUBMISSION_CATEGORY_TO_LABEL } from "common/utils/submissions";
import { getPublicPortalPrefix } from "common/utils/url";
import { colors } from "../../stitches.config";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { AccountLogoOrFallbackIcon } from "../Common/AccountLogo";
import { Icon, IconProps } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import {
  AccountInfo,
  BottomNav,
  Container,
  ExpandedContent,
  Expander,
  HomeButton,
  HoveredSection,
  HoveredSectionContainer,
  HoveredSectionLink,
  ItemWrapper,
  Label,
  StyledNavLink,
} from "./__styles__/SideNav";
import { AccountSwitcher } from "./AccountSwitcher";
import { reducer } from "./useSideNavReducer";

const LabelText = ({ children }: { children: ReactNode }) => {
  return (
    <Body
      size="default"
      type="regular"
      color="contentPrimaryInverted"
      as="span"
    >
      {children}
    </Body>
  );
};

type SideNavWidthContextType = {
  width: number;
};
export const SideNavWidthContext = createContext<SideNavWidthContextType>({
  width: 0,
});

const PublicAccountPortal = ({
  subdomain,
  expanded,
}: {
  subdomain: string;
  expanded: boolean;
}) => {
  const { account } = useContext(AuthContext);

  if (!account?.publicPortal.enabled) return null;

  const link = buildFullLink("guestHome", {
    prefix: getPublicPortalPrefix(subdomain),
    params: {},
  });

  return (
    <StyledNavLink
      as="a"
      href={link}
      data-testid="public-account-portal-link"
      target="_blank"
      onClick={() => track("Public website button clicked")}
    >
      <Label expanded={expanded}>
        <Icon
          color="contentPrimaryInverted"
          iconName="panels-top-left"
          size="16"
        />
        <LabelText>Public site</LabelText>
      </Label>
    </StyledNavLink>
  );
};

const AccountSwitcherNavItem = ({ expanded }: { expanded: boolean }) => {
  const timeoutRef = useRef<Maybe<NodeJS.Timeout>>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 50);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <StyledNavLink as="a">
        <Label
          aria-label="Account Links"
          expanded={expanded}
          hideLabelTextOnHover
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Icon
            color="contentPrimaryInverted"
            iconName="arrow-right-square"
            size="16"
          />
          <LabelText>Account</LabelText>
        </Label>
      </StyledNavLink>
      <ExpandedContent
        expanded={expanded}
        isHovered={isHovered}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <AccountSwitcher />
      </ExpandedContent>
    </>
  );
};

const trackNavLinkClick = (location: string) => {
  track("Sidetab nav clicked", { location });
};

export type SideNavProps = {
  setSideNavWidth?: (width: number) => void;
};

const SideNav = ({ setSideNavWidth }: SideNavProps) => {
  const { account, isFeatureEnabled, admin } = useContext(AuthContext);

  const [
    { sideNavExpanded, recordsExpanded, objectsExpanded, workflowsExpanded },
    dispatch,
  ] = useReducer(reducer, {
    sideNavExpanded: false,
    recordsExpanded: false,
    objectsExpanded: false,
    workflowsExpanded: false,
  });
  const sideNavRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sideNavRef.current && setSideNavWidth) {
      setSideNavWidth(sideNavRef.current.offsetWidth);
    }
  }, [
    sideNavExpanded,
    recordsExpanded,
    objectsExpanded,
    workflowsExpanded,
    setSideNavWidth,
  ]);

  if (!account) {
    return null;
  }

  const hasWorkflowSavedViews = account.savedViews.some(
    view => view.query.source.table === "Workflows",
  );
  const hasWorkflows = account.workflowDefinitions.length > 0;

  const categories = new Set(
    account.submissionTypes.map(type => type.category),
  );

  const hasPropertyTable = isFeatureEnabled(FEATURE_FLAG_NAME.PROPERTY_TABLE);
  const logsEnabled = isFeatureEnabled(FEATURE_FLAG_NAME.LOGS);
  const objectsTabItems =
    account.customObjectGroups
      .filter(group => group.hasTable)
      .map(group => ({
        url: buildLink("objectsTable", { groupId: group.id }),
        display: group.name,
      })) ?? [];

  if (hasPropertyTable) {
    // properties table goes first
    objectsTabItems.unshift({
      url: getPath("properties"),
      display: "Properties",
    });
  }

  const recordsTabItems = [];

  if (logsEnabled) {
    recordsTabItems.push({
      url: getPath("logs"),
      display: "Logs",
    });
  }

  [...categories].forEach(category => {
    recordsTabItems.push({
      url: buildLink("recordsTable", { category }),
      display: SUBMISSION_CATEGORY_TO_LABEL[category],
    });
  });

  const workflowsTabItems = account.workflowDefinitions.map(
    workflowDefinition => ({
      url: buildLink("workflowsTable", {
        workflowDefinitionId: workflowDefinition.id,
      }),
      display: workflowDefinition.name,
    }),
  );

  return (
    <Container ref={sideNavRef} expanded={sideNavExpanded}>
      <AccountInfo>
        <HomeButton
          to={getPath("map")}
          onClick={async () => {
            if (admin) {
              await navigator.clipboard.writeText(account.id);
            }
          }}
        >
          <AccountLogoOrFallbackIcon account={account} />
        </HomeButton>
        <ItemWrapper>
          <Expander onClick={() => dispatch({ toggle: "sideNavExpanded" })}>
            <Icon
              color="contentPrimaryInverted"
              iconName={sideNavExpanded ? "chevron-left" : "chevron-right"}
              size="16"
              testId="side-nav-expander"
            />
          </Expander>
        </ItemWrapper>
      </AccountInfo>

      <StyledNavLink
        to={getPath("map")}
        onClick={() => trackNavLinkClick("map")}
      >
        <Label expanded={sideNavExpanded}>
          <Icon color="contentPrimaryInverted" iconName="earth" size="16" />
          <LabelText>Map</LabelText>
        </Label>
      </StyledNavLink>
      <Tab
        label="Objects"
        horizontallyExpanded={sideNavExpanded}
        verticallyExpanded={objectsExpanded}
        toggleTab={() => dispatch({ toggle: "objectsExpanded" })}
        items={objectsTabItems}
        iconName={
          objectsTabItems.length === 1 && hasPropertyTable ?
            "home"
          : "map-pinned"
        }
      />
      <Tab
        label="Records"
        horizontallyExpanded={sideNavExpanded}
        verticallyExpanded={recordsExpanded}
        toggleTab={() => dispatch({ toggle: "recordsExpanded" })}
        items={recordsTabItems}
        iconName="library"
      />

      {hasWorkflows && hasWorkflowSavedViews && (
        <Tab
          label="Workflows"
          horizontallyExpanded={sideNavExpanded}
          verticallyExpanded={workflowsExpanded}
          toggleTab={() => dispatch({ toggle: "workflowsExpanded" })}
          items={workflowsTabItems}
          iconName="folder-closed"
        />
      )}

      <StyledNavLink
        to={getPath("documentUploads")}
        onClick={() => trackNavLinkClick("files")}
      >
        <Label expanded={sideNavExpanded}>
          <Icon color="contentPrimaryInverted" iconName="file-text" size="16" />
          <LabelText>Files</LabelText>
        </Label>
      </StyledNavLink>

      {isFeatureEnabled(FEATURE_FLAG_NAME.TASKS_WEB) && (
        <StyledNavLink
          to={getPath("tasksTable")}
          onClick={() => trackNavLinkClick("tasks")}
        >
          <Label expanded={sideNavExpanded}>
            <Icon
              color="contentPrimaryInverted"
              iconName="clipboard-check"
              size="16"
            />
            <LabelText>Tasks</LabelText>
          </Label>
        </StyledNavLink>
      )}
      <StyledNavLink
        to={getPath("settings")}
        onClick={() => trackNavLinkClick("settings")}
      >
        <Label expanded={sideNavExpanded}>
          <Icon color="contentPrimaryInverted" iconName="settings" size="16" />
          <LabelText>Settings</LabelText>
        </Label>
      </StyledNavLink>

      <BottomNav>
        <PublicAccountPortal
          subdomain={account.subdomain}
          expanded={sideNavExpanded}
        />
        <AccountSwitcherNavItem expanded={sideNavExpanded} />
      </BottomNav>
    </Container>
  );
};
export default SideNav;

const Tab = ({
  label,
  horizontallyExpanded,
  verticallyExpanded,
  toggleTab,
  items,
  iconName,
}: {
  label: string;
  horizontallyExpanded: boolean;
  verticallyExpanded: boolean;
  toggleTab: () => void;
  items: { url: string; display: string }[];
  iconName: IconProps["iconName"];
}) => {
  const { pathname } = useLocation();
  const onTab = items.map(item => item.url).includes(pathname);
  const [hovered, setHovered] = useState(false);

  if (items.length === 0) {
    return null;
  }

  if (arrayHasExactlyOneItem(items)) {
    return (
      <StyledNavLink
        to={items[0].url}
        onClick={() => trackNavLinkClick(lowerCase(items[0].display))}
      >
        <Label expanded={horizontallyExpanded}>
          <div>
            <Icon
              color="contentPrimaryInverted"
              iconName={iconName}
              size="16"
            />
          </div>
          <LabelText>{items[0].display}</LabelText>
        </Label>
      </StyledNavLink>
    );
  }

  return (
    <ItemWrapper
      expanded={verticallyExpanded}
      data-testid={`${lowerCase(label)}-item-wrapper`}
      // Because the icon isn't clickable when sidenav is collapsed,
      // we use default cursor to signify no click behavior
      style={{
        cursor: horizontallyExpanded ? "pointer" : "default",
        backgroundColor:
          !verticallyExpanded && onTab ? colors.grey500.value : "transparent",
      }}
    >
      <Label
        expanded={horizontallyExpanded}
        onClick={() => {
          // only toggle the tab if horizontally expanded
          if (horizontallyExpanded) {
            toggleTab();
          }
        }}
        nested={false}
      >
        <Icon
          color="contentPrimaryInverted"
          iconName={iconName}
          size="16"
          testId={`${lowerCase(label)}-icon`}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        />
        {horizontallyExpanded && <LabelText>{label}</LabelText>}
        {horizontallyExpanded && (
          <Icon
            color="contentPrimaryInverted"
            iconName={verticallyExpanded ? "chevron-up" : "chevron-down"}
            size="16"
          />
        )}
      </Label>

      {!horizontallyExpanded && (
        <HoveredSectionContainer role="menu" aria-hidden={!hovered}>
          <HoveredSection>
            <LabelText>{label}</LabelText>
          </HoveredSection>
          {items.map((item, index) => (
            <HoveredSectionLink
              key={index}
              to={item.url}
              onClick={() => trackNavLinkClick(lowerCase(item.display))}
              last={index === items.length - 1}
            >
              <LabelText>{item.display}</LabelText>
            </HoveredSectionLink>
          ))}
        </HoveredSectionContainer>
      )}
      {verticallyExpanded &&
        items.map((item, index) => (
          <StyledNavLink
            key={index}
            to={item.url}
            onClick={() => trackNavLinkClick(lowerCase(item.display))}
          >
            <Label expanded nested>
              <LabelText>{item.display}</LabelText>
            </Label>
          </StyledNavLink>
        ))}
    </ItemWrapper>
  );
};
